import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_BlurImage = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/blur-image',
			name: 'Blur Image online — Selective Photo Blur Tool | Lift',
			description:
				"Blur a picture online, hide sensitive areas, or soften backgrounds with Lift's blur tool. Upload, select the region, adjust intensity, and download. Try it.",
			url: 'https://liftapp.ai/blur-image',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/blur-image#product' },
				{ '@id': 'https://liftapp.ai/blur-image#howto' },
				{ '@id': 'https://liftapp.ai/blur-image#benefits' },
				{ '@id': 'https://liftapp.ai/blur-image#faq' },
				{ '@id': 'https://liftapp.ai/blur-image#why-lift' },
				{ '@id': 'https://liftapp.ai/blur-image#explore-tools' },
				{ '@id': 'https://liftapp.ai/blur-image#all-in-one' },
			],
		},
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/blur-image#product',
			name: 'Blur Image Online',
			description:
				"Blur a picture online, hide sensitive areas, or soften backgrounds with Lift's blur tool. Upload, select the region, adjust intensity, and download.",
			url: 'https://liftapp.ai/blur-image',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: 4.8,
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/blur-image#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your image',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Select the area to blur',
					text: 'Target the background, a specific object, or use the manual brush to blur any portion of the image',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Adjust blur intensity',
					text: 'Use the slider to control blur strength — from a subtle softening to a full blur across the selected area',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/blur-image#benefits',
			name: "Who can benefit from Lift's blur tool?",
			description:
				"Lift's blur image tool is useful for content creators, marketers, small business owners, privacy-conscious users, and casual users who want to blur any part of a photo online.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Content creators — Direct audience attention by blurring backgrounds and creating visual depth in photos.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Marketers & SMM managers — Remove distracting elements from product visuals by blurring specific areas of a photo.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Small business owners — Produce polished, professional images without hiring a designer or purchasing desktop software.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Privacy-Conscious Users — Blur sensitive details in screenshots, documents, or personal photos before sharing online.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Casual Users — Blur your photo online in seconds with no editing experience or technical knowledge required.',
				},
			],
		},
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/blur-image#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: "What is Lift's blur image tool?",
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Lift's blur image tool lets you selectively blur any part of a photo — background, specific objects, or freehand regions using a manual brush. It runs entirely in the browser, requires no software installation, and works across all major image formats.",
					},
				},
				{
					'@type': 'Question',
					name: "How does Lift's blur tool work?",
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Upload your photo, choose the area to blur — background, a specific object, or a custom region — then set the blur intensity using the slider. Once you're satisfied with the result, download the image in full resolution.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Yes. Lift's blur image tool uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking results without manual touch-ups.",
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/blur-image#why-lift',
			name: "Why Lift's blur tool stands out",
			description:
				"Lift's blur tool combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/blur-image#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/blur-image#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in/?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in/?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Blur Image Online',
					item: 'https://liftapp.ai/blur-image',
				},
			],
		},
	],
};
